import type { CSSProperties } from 'react';

import { t } from '@/i18n';

import { gradeColorVars } from './grade-palette';

/**
 * 사용자 등급 표시·비교 (TRAD-2162).
 *
 * ## 서버가 내려주는 값은 **PascalCase** 다
 * ```java
 * // TradingAppServer  common/constants/UserGrade.java
 * NOVICE("Novice"), …
 * @JsonValue private String code;   // ← name() 이 아니라 code 로 직렬화
 * ```
 * `@JsonValue` 때문에 **모든 엔드포인트**가 `"Novice"` 를 보낸다(로그인 `userRes.userGrade` ·
 * 게시판/피드 `cretUserGrade` · 댓글 `createdByUserGrade` · 부동산 커뮤니티 — dev 실호출 확인).
 * 그런데 웹 타입은 `'NOVICE'` 대문자로 선언돼 있어 **타입과 실제 값이 서로 어긋난다.**
 *
 * ## 여기서 두 가지를 막는다
 * 1. **표시** — `getGradeLabel()` 은 `normalizeGrade()` 를 거치므로 대문자·PascalCase 어느
 *    표기로 와도 같은 라벨이 나온다. 스타일 맵(`GRADE_STYLE`)도 양쪽 키를 **생성**해서 갖는다.
 *    ⚠️ 예전에는 두 표기를 **손으로** 나열해 뒀다. 등급이 추가될 때 한쪽에만 넣으면 그 등급만
 *    조용히 폴백으로 떨어진다. 지금은 한 벌에서 생성하므로 갈릴 수 없다.
 *    🔴 라벨 문구 자체는 TRAD-2238 에서 **i18n 카탈로그로 이전**했다(`grade.label.*`).
 *    하드코딩 한글 맵은 EN 로케일에서도 항상 한글을 내보내 영어 사용자에게 새어 나갔다.
 * 2. **비교** — `userGrade !== 'BEGINNER'` 같은 문자열 직접 비교는 서버가 `"Beginner"` 를
 *    보내므로 **항상 어긋난다.** 등급 게이트가 조용히 열린다(이 티켓의 실제 피해).
 *    비교가 필요하면 반드시 `isGrade()` / `normalizeGrade()` 를 쓸 것.
 *
 * 🔴 **서버를 대문자로 바꾸는 선택지는 없다.** iOS 가 `case novice = "Novice"` 로 이미
 * 배포돼 있어 서버를 바꾸면 iOS 가 깨진다. android 는 `safeValueOf(value.uppercase())` 로
 * 정규화하고 있어 무관하다. 즉 **웹이 맞추는 것이 맞다.**
 */

/** 서버가 실제로 직렬화하는 값(=`UserGrade.java` 의 `code`). 낮은 등급 → 높은 등급 순. */
export const USER_GRADES = [
  'Beginner',
  'Novice',
  'Intermediate',
  'Advanced',
  'Expert',
  'Master',
  'Maestro',
] as const;

/** 서버 표기 기준 등급 값. */
export type GradeCode = (typeof USER_GRADES)[number];

/**
 * 🔴 TRAD-2238 완료 — 하드코딩 한글 라벨 맵(`LABEL_BY_GRADE` + `GRADE_LABEL`)을 **제거했다.**
 *
 * 그 맵은 로케일과 무관하게 **항상 한글**을 돌려줘서, EN 로케일 사용자에게 등급명만
 * 한글로 떴다(댓글·프로필·카드 등 등급을 보여주는 모든 화면). 이제 등급 표시는
 * `getGradeLabel()` / `localizeGradeNames()` 하나로만 나온다 — i18n 카탈로그
 * `grade.label.<GradeCode>` 경유.
 *
 * 🔴 **여기에 라벨 맵을 다시 만들지 말 것.** 등급명이 필요하면 `getGradeLabel(value)` 를
 * 쓴다. 손으로 복제한 등급 맵이 갈리는 사고가 반복됐다(마이페이지가 자체 사본을 갖고
 * 있다가 Maestro 만 빠져 영문으로 남았던 전례, 등급 체계 표가 자체 영문 리터럴을 갖고
 * 있어 KO 화면에서 요약 카드와 갈렸던 전례).
 *
 * 스타일 맵(`GRADE_STYLE`)은 CSS 클래스만 돌려주므로 i18n 과 무관해 그대로 둔다.
 */
// 🟢 TRAD-3156 — ~~STYLE_BY_GRADE(Tailwind 팔레트 bg-*-100/text-*-700, Master·Maestro 둘 다 red)~~ 삭제.
// 등급 색은 정본 사본 `grade-palette.json` → `grade-palette.ts`(GRADE_PALETTE · gradeColorVars) 한 곳에서만 나온다.
// 여기에 색 맵을 다시 만들지 말 것(정본 md 규칙 3 — grade-badge-scan 이 막는다).

/**
 * 한 벌의 정의에서 대문자·PascalCase 키를 **생성**한다.
 * 손으로 두 벌 적으면 등급 추가 시 한쪽에만 들어가고, 그 등급만 조용히 폴백된다.
 */
const withBothCases = <T,>(source: Record<GradeCode, T>): Record<string, T> =>
  Object.fromEntries(
    USER_GRADES.flatMap((grade) => [
      [grade, source[grade]],
      [grade.toUpperCase(), source[grade]],
    ]),
  );

/**
 * 등급 → 그 등급의 CSS 변수(정본 hex). 대문자·PascalCase 양쪽 키를 생성한다(종전 계약 유지 — 소비처는 GradeBadge).
 * 🟢 TRAD-3156 — ~~{ bg, text } Tailwind 클래스~~ → 정본 팔레트 변수. 색 클래스는 `GRADE_BADGE_COLOR_CLASS` 하나.
 */
export const GRADE_STYLE: Record<string, CSSProperties> = withBothCases(
  Object.fromEntries(USER_GRADES.map((grade) => [grade, gradeColorVars(grade)])) as Record<GradeCode, CSSProperties>,
);

/**
 * 어떤 표기로 오든 서버 표기(PascalCase)로 맞춘다. 아는 등급이 아니면 `undefined`.
 *
 * android 의 `UserGrade.safeValueOf(value.uppercase())` 와 같은 역할이다 — 표기 차이를
 * 클라가 흡수한다. 모르는 값에 예외를 던지지 않는 것이 중요하다: 서버가 등급을 추가해도
 * 화면이 죽으면 안 된다.
 */
export const normalizeGrade = (value: string | null | undefined): GradeCode | undefined => {
  if (!value) return undefined;
  const upper = value.toUpperCase();
  return USER_GRADES.find((grade) => grade.toUpperCase() === upper);
};

/**
 * 등급 비교. **문자열 직접 비교(`userGrade === 'BEGINNER'`) 대신 반드시 이것을 쓸 것.**
 *
 * 직접 비교는 서버가 `"Beginner"` 를 보내므로 항상 어긋나고, 등급 게이트가 조용히
 * 열리거나 닫힌다. 타입이 대문자로 선언돼 있어 타입체커도 안 잡아준다.
 */
export const isGrade = (value: string | null | undefined, grade: GradeCode): boolean =>
  normalizeGrade(value) === grade;

// TRAD-2238 — 등급 표시 문구를 i18n 카탈로그(`grade.label.<GradeCode>`)로 이전한다.
// 🔴 t()는 반드시 "호출 시점"에만 부른다 — 모듈 스코프 상수에서 t()를 불러 캐시하면
// 그 값이 모듈 로드 시점 로케일로 굳어버려 이후 로케일 전환이 반영되지 않는다. 그래서
// 상수(`GRADE_LABEL_KEY`)엔 등급별 **i18n 키**만 두고, 실제 문구는 호출될 때마다
// `resolveGradeLabel()`을 거쳐 그때그때 뽑는다.
const GRADE_LABEL_KEY: Record<GradeCode, Parameters<typeof t>[0]> = {
  Beginner: 'grade.label.Beginner',
  Novice: 'grade.label.Novice',
  Intermediate: 'grade.label.Intermediate',
  Advanced: 'grade.label.Advanced',
  Expert: 'grade.label.Expert',
  Master: 'grade.label.Master',
  Maestro: 'grade.label.Maestro',
};

// 🔴 `getGradeLabel`(값 하나 표시)과 `localizeGradeNames`(문장 안에 섞인 등급명 치환)는
// 반드시 이 하나의 함수를 공유해야 한다 — 물리적으로 같은 소스를 타야 "같은 등급은
// 어디서 보든 같은 문구"가 보장된다. 갈라지면(예: 한쪽만 옛 하드코딩 맵을 쓰면) 등급
// 뱃지는 새 문구인데 포인트 이력 문장만 옛 문구로 남는 회귀가 재발한다(마이페이지가
// 한때 자체 사본을 갖고 있다가 Maestro가 빠졌던 전례와 같은 유형의 사고).
const resolveGradeLabel = (grade: GradeCode): string => t(GRADE_LABEL_KEY[grade]);

/**
 * 등급 코드 하나를 로케일에 맞는 표시 문구로 바꾼다. 모르는 값(서버 계약 드리프트 등)은
 * 기존 `GRADE_LABEL[x] ?? x` 호출부와 동일하게 원본 값을 그대로 반환한다(화면이 죽지 않음).
 */
export const getGradeLabel = (value: string | null | undefined): string => {
  const grade = normalizeGrade(value);
  return grade ? resolveGradeLabel(grade) : value ?? '';
};

/**
 * 문장 **안에 섞여 있는** 등급명을 로케일에 맞는 표시 문구로 바꾼다.
 *
 * 서버가 등급 변경 사유를 문장으로 내려주는데 그 안에 등급 코드가 그대로 들어 있다:
 * `"Beginner → Novice 승급"`. 값 하나를 라벨로 바꾸는 것(`getGradeLabel(x)`)만으로는
 * 이걸 못 잡아서, 포인트 이력에 원본 등급명이 그대로 노출됐다(실측).
 *
 * ⚠️ 마이페이지가 이 처리를 **자체 라벨 맵 + 인라인 정규식**으로 따로 갖고 있었다.
 * 그 사본에는 `Maestro` 가 빠져 있어서 마에스트로 승급 사유만 조용히 영문으로 남았다 —
 * 손으로 복제한 맵이 갈린 실제 사례다. 여기 하나로 합쳤다.
 *
 * 정규식도 `USER_GRADES` 에서 **생성**하므로 등급이 추가돼도 같이 따라온다.
 */
export const localizeGradeNames = (text: string): string =>
  text.replace(
    new RegExp(`\\b(${USER_GRADES.join('|')})\\b`, 'g'),
    (matched) => {
      const grade = normalizeGrade(matched);
      return grade ? resolveGradeLabel(grade) : matched;
    },
  );
